// Contrato compartilhado entre o check-in e o mapa muscular.
// As chaves são as mesmas chaves lógicas usadas pelo SVG/MuscleMap.

export const CHECKIN_MUSCLES = [
  { key: "chest", label: "Peito" },
  { key: "back", label: "Costas" },
  { key: "shoulders", label: "Ombros" },
  { key: "traps", label: "Trapézio" },
  { key: "biceps", label: "Bíceps" },
  { key: "triceps", label: "Tríceps" },
  { key: "forearms", label: "Antebraços" },
  { key: "core", label: "Core" },
  { key: "quads", label: "Quadríceps" },
  { key: "hamstrings", label: "Posteriores" },
  { key: "glutes", label: "Glúteos" },
  { key: "calves", label: "Panturrilhas" },
  { key: "adductors", label: "Adutores" },
  { key: "abductors", label: "Abdutores" },
  { key: "tibialis", label: "Tibial" },
];

// ── Padrões de recrutamento por modalidade ──────────────────────────────────
// Só entra aqui a modalidade cujo padrão principal é PREVISÍVEL, sessão após
// sessão: corrida sempre usa as mesmas pernas, surfe sempre rema com as mesmas
// costas. O que varia com o treino do dia (musculação, calistenia, CrossFit,
// HIIT, funcional, circuito…) fica FORA — é a pessoa quem diz o que fez.
//
// Base: MET/ACSM + anatomia funcional de cada esporte (músculos motores
// primários e estabilizadores mais carregados). Os grupos são os 15 do mapa.
const RUN = ["quads", "hamstrings", "glutes", "calves", "core", "tibialis"];
const WALK = ["quads", "hamstrings", "glutes", "calves", "core"];
const HIKE = ["quads", "hamstrings", "glutes", "calves", "core", "tibialis"];
const BIKE = ["quads", "hamstrings", "glutes", "calves", "core"];
const MTB = ["quads", "hamstrings", "glutes", "calves", "core", "shoulders", "forearms"];
const ROW = ["back", "shoulders", "biceps", "forearms", "core", "quads", "hamstrings", "glutes"];
const ELLIPTICAL = ["quads", "hamstrings", "glutes", "calves", "core", "back", "shoulders", "triceps"];
const SWIM = ["back", "shoulders", "triceps", "forearms", "core", "glutes", "quads", "hamstrings", "calves"];
const DANCE = ["quads", "hamstrings", "glutes", "calves", "core"];
const STRIKING = ["shoulders", "triceps", "back", "core", "forearms", "quads", "glutes", "calves"];
const KICKING = ["shoulders", "triceps", "back", "core", "quads", "hamstrings", "glutes", "calves"];
const GRAPPLING = ["back", "biceps", "forearms", "shoulders", "core", "quads", "hamstrings", "glutes", "adductors"];
const FIELD_RUN = ["quads", "hamstrings", "glutes", "calves", "core", "adductors", "abductors"];
const CONTACT = ["quads", "hamstrings", "glutes", "calves", "core", "back", "shoulders", "traps"];
const STICK = ["quads", "hamstrings", "glutes", "calves", "core", "back", "shoulders", "forearms"];
const THROW = ["shoulders", "triceps", "back", "core", "quads", "hamstrings", "glutes", "calves"];
const VOLLEY = ["quads", "hamstrings", "glutes", "calves", "core", "shoulders", "triceps", "forearms"];
const BAT = ["shoulders", "forearms", "back", "core", "quads", "glutes", "hamstrings", "triceps"];
const CLIMB = ["forearms", "biceps", "back", "shoulders", "core", "quads", "calves", "glutes"];
const RACKET = ["shoulders", "forearms", "triceps", "back", "core", "quads", "glutes", "calves", "hamstrings"];
const SKATE_ICE = ["quads", "hamstrings", "glutes", "calves", "adductors", "abductors", "core"];

export const FIXED_ACTIVITY_MUSCLES = {
  // Corrida / caminhada / trilha
  corrida: RUN,
  esteira: RUN,
  caminhada: WALK,
  trilha: HIKE,
  mochilagem: [...HIKE, "back", "traps"],

  // Ciclismo
  ciclismo: BIKE,
  spinning: BIKE,
  mountain_bike: MTB,

  // Máquinas de cardio
  eliptica: ELLIPTICAL,
  remo: ROW,

  // Aeróbica
  corda: ["quads", "hamstrings", "glutes", "calves", "core", "shoulders", "forearms"],
  aerobica: DANCE,
  zumba: DANCE,
  danca: DANCE,
  cardio_misto: ["quads", "hamstrings", "glutes", "calves", "core"],
  pole_dance: ["shoulders", "back", "biceps", "forearms", "core", "adductors", "glutes"],
  hidroginastica: ["quads", "hamstrings", "glutes", "calves", "core", "shoulders", "back"],

  // Core
  abdominais: ["core"],
  treino_core: ["core"],

  // Mente e corpo
  yoga: ["core", "shoulders", "back", "glutes", "hamstrings", "quads"],
  pilates: ["core", "glutes", "hamstrings", "quads", "back", "adductors"],
  barre: ["quads", "glutes", "calves", "core", "adductors", "abductors"],
  mente_e_corpo: ["core", "shoulders", "back", "glutes", "hamstrings", "quads"],
  tai_chi: ["quads", "glutes", "hamstrings", "calves", "core"],
  meditacao: [],
  respiracao: [],

  // Artes marciais
  boxe: STRIKING,
  muay_thai: KICKING,
  kickboxing: KICKING,
  artes_marciais: KICKING,
  bjj: GRAPPLING,
  luta: GRAPPLING,

  // Esportes em equipe
  futebol: FIELD_RUN,
  futevolei: [...FIELD_RUN, "shoulders"],
  rugby: CONTACT,
  futebol_americano: CONTACT,
  futebol_australiano: CONTACT,
  hoquei_gelo: [...SKATE_ICE, "shoulders", "forearms"],
  hoquei_grama: STICK,
  lacrosse: STICK,
  basquete: ["quads", "hamstrings", "glutes", "calves", "core", "shoulders", "triceps"],
  handebol: THROW,
  ultimate_frisbee: ["quads", "hamstrings", "glutes", "calves", "core", "shoulders", "forearms"],
  voleibol: VOLLEY,
  volei_praia: VOLLEY,
  polo_aquatico: ["shoulders", "back", "triceps", "core", "quads", "hamstrings", "glutes", "adductors"],
  cricket: BAT,
  beisebol: BAT,
  softbol: BAT,

  // Escalada
  escalada: CLIMB,
  escalada_rocha: CLIMB,
  escalada_indoor: CLIMB,
  boulder: CLIMB,

  // Raquete
  tenis: RACKET,
  beach_tenis: RACKET,
  padel: RACKET,
  squash: RACKET,
  racquetball: RACKET,
  badminton: RACKET,
  pickleball: RACKET,
  paddle: RACKET,

  // Recreativos
  surfe: ["back", "shoulders", "triceps", "core", "quads", "glutes", "calves"],
  caiaque: ["back", "shoulders", "core", "biceps", "forearms", "triceps"],
  patinacao: SKATE_ICE,
  skate: ["quads", "glutes", "hamstrings", "calves", "core", "tibialis"],
  hipismo: ["core", "adductors", "quads", "glutes", "hamstrings", "back"],
  equitacao: ["core", "adductors", "quads", "glutes", "hamstrings", "back"],
  mergulho: ["quads", "hamstrings", "glutes", "calves", "core"],
  golfe: ["core", "back", "shoulders", "forearms", "glutes", "quads"],
  tenis_mesa: ["forearms", "shoulders", "core", "quads", "calves"],
  arco_flecha: ["back", "shoulders", "traps", "forearms", "core"],
  disc_golf: ["shoulders", "forearms", "core", "back", "quads"],
  boliche: ["forearms", "shoulders", "quads", "glutes", "core"],

  // Natação
  natacao: SWIM,
};

// Ficam SELECIONÁVEIS (o que muda com o treino do dia): musculacao,
// calistenia, levantamento_peso, hiit, crossfit, bootcamp, circuito,
// cross_training, funcional, ginastica, jogos_exercicio, esporte_equipe,
// esporte_recreativo, outro.

export function getActivityMusclePreset(activityKey) {
  if (!Object.prototype.hasOwnProperty.call(FIXED_ACTIVITY_MUSCLES, activityKey)) {
    return { mode: "selectable", muscles: [] };
  }
  return { mode: "fixed", muscles: [...FIXED_ACTIVITY_MUSCLES[activityKey]] };
}

export function sanitizeActiveMuscles(value) {
  const allowed = new Set(CHECKIN_MUSCLES.map((m) => m.key));
  return [...new Set((Array.isArray(value) ? value : []).filter((m) => allowed.has(m)))];
}
